/**
 * URL-state codec for shareable calculator scenarios.
 *
 * Format: single `s` query param, base64url-encoded compact JSON.
 *   { p: presetId|null, y: yearsForward, i: [[id,name,catId,rcv,age,qty], ...] }
 */

import type { CalcItem } from "./acvRcv";

const MAX_ITEMS = 100;

interface EncodedState {
  p: string | null;
  y: number;
  i: Array<[string, string, string, number, number, number]>;
}

function base64UrlEncode(s: string): string {
  if (typeof window === "undefined") return "";
  return btoa(unescape(encodeURIComponent(s)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(s: string): string {
  if (typeof window === "undefined") return "";
  let str = s.replace(/-/g, "+").replace(/_/g, "/");
  while (str.length % 4) str += "=";
  try {
    return decodeURIComponent(escape(atob(str)));
  } catch {
    return "";
  }
}

export function encodeState(presetId: string | null, items: CalcItem[], yearsForward: number): string {
  const trimmed = items.slice(0, MAX_ITEMS);
  const enc: EncodedState = {
    p: presetId,
    y: yearsForward,
    i: trimmed.map((it) => [it.id, it.name, it.categoryId, it.rcv, it.ageYears, it.qty]),
  };
  return base64UrlEncode(JSON.stringify(enc));
}

export function decodeState(token: string | null): {
  presetId: string | null;
  items: CalcItem[];
  yearsForward: number;
} | null {
  if (!token) return null;
  const json = base64UrlDecode(token);
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as EncodedState;
    if (!Array.isArray(parsed.i)) return null;
    return {
      presetId: typeof parsed.p === "string" ? parsed.p : null,
      yearsForward: typeof parsed.y === "number" ? parsed.y : 0,
      items: parsed.i.slice(0, MAX_ITEMS).map((tup) => ({
        id: String(tup[0]),
        name: String(tup[1]),
        categoryId: String(tup[2]),
        rcv: Number(tup[3]) || 0,
        ageYears: Number(tup[4]) || 0,
        qty: Number(tup[5]) || 1,
      })),
    };
  } catch {
    return null;
  }
}
